import {
  queryOptions,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import {
  createWorkspace,
  listWorkspaces,
  readWorkspace,
} from '@/lib/api/generated/api';
import type { WorkspaceAggregate } from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';
import { workspaceKeys } from '../queryKeys';

/**
 * These call the generated *functions* rather than the generated hooks.
 *
 * Not a workaround any more — orval now emits the right hook kind per method.
 * The reason is that each of these adds something the generated hook has no way
 * to know: which status counts as success, what a failure should say, and what
 * else in the cache a success invalidates. `expectStatus` carries the envelope
 * handling so what is left in each hook is only that decision.
 */

/**
 * Every workspace the service holds, newest first.
 *
 * One request, however many entries — each carries the name and status needed
 * to render it, so nothing here reads a workspace to find out what it is
 * called.
 *
 * Anything but a 200 throws rather than resolving to an empty page. That
 * distinction is the point: a caller must be able to tell "no workspaces exist"
 * from "we could not find out", and collapsing the two into `[]` would render a
 * confident, wrong empty state.
 *
 * `enabled` defaults to `true`. The sidebar switcher passes `false` and mounts
 * the query closed, fetching only on first open — the shell still performs
 * exactly one read (the current workspace's) until someone actually opens the
 * switcher.
 */
export function useWorkspaceList(options?: {
  enabled?: boolean;
  /**
   * How many entries to ask for. Omitted, the service applies its own default.
   * The list page raises it when someone asks to see past the first page — the
   * contract has carried `limit` and `offset` all along, and calling without
   * them was what made "Showing 50 of 137" a statement with no way to act on
   * it.
   */
  limit?: number;
}) {
  const { limit } = options ?? {};

  return useQuery({
    queryKey: workspaceKeys.list(limit),
    queryFn: async () =>
      expectStatus(
        // The enrichments are asked for explicitly (spec 070). They are opt-in
        // precisely so the default response stays what it was for every other
        // caller; the front door is the one screen that renders them.
        //
        // Ordered by activity on the service, not here: a workspace's own
        // `updated_at` moves only when it is edited, and re-sorting one page in
        // the browser could never surface a busy workspace from the next one.
        await listWorkspaces({
          ...(limit === undefined ? {} : { limit }),
          // `access` divides the front door into yours, shared and the rest (spec 188).
          include: ['counts', 'workflow_names', 'last_activity', 'access'],
          sort: 'activity',
        }),
        200,
        'The workspaces could not be loaded.',
      ),
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string): Promise<WorkspaceAggregate> =>
      expectStatus(
        await createWorkspace({ name }),
        201,
        'The workspace could not be created. Check the name and try again.',
      ),
    onSuccess: (workspace) => {
      // Seed the cache so entering the workspace does not refetch what the
      // create call already returned.
      queryClient.setQueryData(workspaceKeys.detail(workspace.id), workspace);
      // The collection now has one more member. The service owns that list, so
      // refetch it rather than splicing the new workspace in locally and hoping
      // the ordering matches.
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.list() });
    },
  });
}

/**
 * Shared query definition for a single workspace's aggregate, used by
 * `useWorkspace` below and by route loaders that need the same data before
 * a component mounts (e.g. the boards route resolving its default board).
 * One definition, so cache identity and fetch behaviour cannot drift between
 * the two call sites.
 *
 * A workspace that cannot be read resolves to `null` instead of throwing, and
 * that asymmetry with the list query above is deliberate. The shell renders
 * this alongside real content: a missing name should leave the badge blank,
 * not put the surrounding page into an error state.
 *
 * `refetchInterval` is optional and defaults to false (no polling). Callers may
 * pass a number (milliseconds) or false to enable/disable polling. When set, polling
 * must respect the constitution's rules: pause when document is hidden
 * (`refetchIntervalInBackground: false`, already the default in queryClient.ts),
 * and be conditional on actual need (e.g., non-terminal task activity).
 */
export function workspaceQueryOptions(
  workspaceId: string,
  options?: { refetchInterval?: number | false; includeHidden?: boolean },
) {
  const includeHidden = options?.includeHidden ?? false;
  return queryOptions({
    queryKey: workspaceKeys.detail(workspaceId, includeHidden),
    queryFn: async ({ client, queryKey }) => {
      const response = await readWorkspace(
        workspaceId,
        includeHidden ? { include_hidden: true } : {},
      );
      // What the read said, for the layout's no-access and not-found pages
      // (spec 188): kept beside the aggregate rather than in it, so every other
      // reader keeps its `null`.
      client.setQueryData(
        workspaceKeys.refusal(workspaceId),
        response.status === 200 ? null : refusalOf(response),
      );
      if (response.status === 200) return response.data;
      /*
        A *refresh* that fails keeps what was read before (spec 126 FR-007):
        throwing leaves the cached aggregate in place and marks the query as a
        failed refetch, which the page says as "Couldn't refresh". Only a first
        read with nothing to keep resolves to `null`, the page's "could not be
        loaded" — and the shell's blank badge, as before.
      */
      if (client.getQueryData(queryKey)) {
        throw new Error('The workspace could not be refreshed.');
      }
      return null;
    },
    retry: false,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Why a workspace could not be read: `not_accessible` for a workspace this person
 * may not see at all (a Private one they are not in, spec 151 FR-006), `not_found`
 * for one that does not exist, `null` otherwise — including any other failure,
 * which stays each page's "could not be loaded".
 */
export type WorkspaceRefusal = 'not_accessible' | 'not_found';

function refusalOf(response: {
  status: number;
  data: unknown;
}): WorkspaceRefusal | null {
  if (response.status === 404) return 'not_found';
  const data = response.data;
  if (
    response.status === 403 &&
    data !== null &&
    typeof data === 'object' &&
    'error' in data &&
    data.error === 'WORKSPACE_NOT_ACCESSIBLE'
  ) {
    return 'not_accessible';
  }
  return null;
}

/** The refusal the last read of this workspace met, if it was one (spec 188). */
export function useWorkspaceRefusal(
  workspaceId: string,
): WorkspaceRefusal | null {
  const { data } = useQuery<WorkspaceRefusal | null>({
    queryKey: workspaceKeys.refusal(workspaceId),
    // Written by the workspace read; nothing to fetch on its own.
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });
  return data ?? null;
}

/** Reads one workspace. Used by the shell to show a name rather than a UUID. */
export function useWorkspace(
  workspaceId: string,
  options?: {
    refetchInterval?: number | false;
    includeHidden?: boolean;
    /**
     * While switching between reading with and without hidden tasks, keep
     * showing what was read before rather than nothing — the same workspace,
     * one read behind (spec 112). Never across workspaces: another
     * workspace's tasks, even for a moment, would be wrong, not stale.
     */
    keepWhileSwitchingHidden?: boolean;
    /**
     * `'always'` on the monitoring pages (spec 126 R1): coming back to the tab
     * reads at once, whatever the cache's age.
     */
    refetchOnWindowFocus?: boolean | 'always';
  },
) {
  /*
    Polling stands still while any write is in flight.

    Every optimistic task write patches this exact query key, and the board polls
    it every two seconds. `cancelQueries` in `onMutate` only cancels what is
    already running — a poll tick that *starts* after the patch and resolves
    before the server answers would land stale data on top of the optimistic
    update, showing the old assignee or title for a moment before the response
    corrected it. Raised in review on MR !69.

    Deliberately `useIsMutating()` with no filter: any mutation this app makes is
    short, and a poll suspended for the length of one write costs nothing next to
    reasoning about which mutations touch which slice of the aggregate.
  */
  const writesInFlight = useIsMutating();
  return useQuery({
    ...workspaceQueryOptions(workspaceId, {
      refetchInterval: writesInFlight > 0 ? false : options?.refetchInterval,
      includeHidden: options?.includeHidden,
    }),
    ...(options?.refetchOnWindowFocus !== undefined
      ? { refetchOnWindowFocus: options.refetchOnWindowFocus }
      : {}),
    ...(options?.keepWhileSwitchingHidden
      ? {
          placeholderData: (
            previous: WorkspaceAggregate | null | undefined,
            previousQuery: { queryKey: readonly unknown[] } | undefined,
          ) =>
            previousQuery?.queryKey[1] === workspaceId ? previous : undefined,
        }
      : {}),
  });
}

/**
 * Marks one workspace's cached aggregate stale and refetches it.
 *
 * Every surface showing workspace content renders from that one read, so this
 * is what makes a change appear everywhere at once without a reload. The
 * returned promise settles when fresh data has arrived, so a caller can keep
 * showing a pending state until the change is actually visible.
 *
 * A named behaviour rather than the key it uses: a caller importing
 * `workspaceKeys` would depend on this feature's cache layout (Principle 1).
 */
export function invalidateWorkspace(
  queryClient: QueryClient,
  workspaceId: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: workspaceKeys.detail(workspaceId),
  });
}

/**
 * Warms one workspace's aggregate, for a route loader to call before its
 * component renders, so entering a route does not waterfall into a spinner.
 */
export function prefetchWorkspace(
  queryClient: QueryClient,
  workspaceId: string,
): Promise<void> {
  return queryClient.prefetchQuery(workspaceQueryOptions(workspaceId));
}
